import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

import type { ToastRecord, ToastTone } from './toastContext';
import styles from './Toast.module.css';

const TONE_GLYPH: Readonly<Record<ToastTone, string>> = {
  neutral: '•',
  success: '✓',
  warning: '!',
  danger: '×',
  info: 'i',
};

/** Distance or speed past which a released drag dismisses instead of springing back. */
const DISMISS_DISTANCE_PX = 96;
const DISMISS_SPEED = 0.45; // px per ms

export interface ToastProps {
  toast: ToastRecord;
  /** `immediate`: the toast already animated itself away (a swipe). */
  onDismiss: (id: string, immediate?: boolean) => void;
  dismissLabel?: string | undefined;
  /** Reports the toast's natural height, for the stack's layout. */
  onHeight?: ((id: string, height: number) => void) | undefined;
  /** A tap that was not a drag (touch): opens or closes the stack. */
  onTap?: (() => void) | undefined;
  /** Only the front toast, or any toast of an open stack, can be dragged away. */
  draggable?: boolean | undefined;
  leaving?: boolean | undefined;
  /** Behind the front toast in a collapsed stack: only its edge shows. */
  back?: boolean | undefined;
  style?: CSSProperties | undefined;
}

interface Drag {
  pointer: number;
  x: number;
  y: number;
  axis: 'x' | 'y' | null;
  distance: number;
  samples: { t: number; d: number }[];
}

export function Toast({
  toast,
  onDismiss,
  dismissLabel = 'Dispensar aviso',
  onHeight,
  onTap,
  draggable = true,
  leaving = false,
  back = false,
  style,
}: ToastProps) {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<Drag | null>(null);
  const [offset, setOffset] = useState<{ axis: 'x' | 'y'; d: number } | null>(null);
  const [swiped, setSwiped] = useState<'x' | 'y' | null>(null);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (outer === null || inner === null || onHeight === undefined) {
      return;
    }
    // The content's own height plus the card's borders: the stack sets the card's height, so the
    // card itself cannot be measured.
    const report = () => {
      onHeight(toast.id, inner.offsetHeight + outer.offsetHeight - outer.clientHeight);
    };
    report();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(report);
    observer.observe(inner);
    return () => {
      observer.disconnect();
    };
  }, [onHeight, toast.id]);

  // The drag rides on `translate`, so it composes with the stack's own `transform`.
  const translate =
    swiped === 'x'
      ? '110% 0'
      : swiped === 'y'
        ? '0 120%'
        : offset === null
          ? undefined
          : offset.axis === 'x'
            ? `${String(offset.d)}px 0`
            : `0 ${String(offset.d)}px`;

  return (
    <div
      ref={outerRef}
      className={[styles.toast, styles[toast.tone]].join(' ')}
      data-leaving={leaving ? '' : undefined}
      data-back={back ? '' : undefined}
      data-dragging={offset !== null && swiped === null ? '' : undefined}
      data-swiped={swiped ?? undefined}
      style={{ ...style, translate }}
      onPointerDown={(event) => {
        if (event.button !== 0 || (event.target as Element).closest('button') !== null) {
          return;
        }
        drag.current = {
          pointer: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          axis: null,
          distance: 0,
          samples: [{ t: event.timeStamp, d: 0 }],
        };
      }}
      onPointerMove={(event) => {
        const current = drag.current;
        if (current === null || current.pointer !== event.pointerId || !draggable) {
          return;
        }
        const dx = event.clientX - current.x;
        const dy = event.clientY - current.y;
        if (current.axis === null) {
          if (Math.hypot(dx, dy) < 6) {
            return;
          }
          current.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
          event.currentTarget.setPointerCapture(event.pointerId);
        }
        const raw = current.axis === 'x' ? dx : dy;
        // Follows the finger toward right or down; against it, a heavy rubber band.
        const d = raw >= 0 ? raw : raw / 6;
        current.distance = d;
        current.samples = [...current.samples.slice(-5), { t: event.timeStamp, d }];
        setOffset({ axis: current.axis, d });
      }}
      onPointerUp={(event) => {
        const current = drag.current;
        drag.current = null;
        if (current === null || current.pointer !== event.pointerId) {
          return;
        }
        if (current.axis === null) {
          if (event.pointerType !== 'mouse') {
            onTap?.();
          }
          return;
        }
        // Speed over the last moments before release, so a finger that stopped is not a flick.
        const upAt = event.timeStamp;
        const recent = current.samples.find((sample) => sample.t >= upAt - 100) ?? {
          t: upAt,
          d: current.distance,
        };
        const speed = (current.distance - recent.d) / Math.max(16, upAt - recent.t);
        if (
          current.distance > 6 &&
          (current.distance > DISMISS_DISTANCE_PX || speed > DISMISS_SPEED)
        ) {
          setSwiped(current.axis);
          setTimeout(() => {
            onDismiss(toast.id, true);
          }, 200);
        } else {
          setOffset(null);
        }
      }}
      onPointerCancel={() => {
        drag.current = null;
        setOffset(null);
      }}
    >
      <div className={styles.inner} ref={innerRef}>
        <span className={styles.icon} aria-hidden="true">
          {TONE_GLYPH[toast.tone]}
        </span>
        <div className={styles.content}>
          <p className={styles.title}>{toast.title}</p>
          {toast.description !== undefined ? (
            <p className={styles.description}>{toast.description}</p>
          ) : null}
        </div>
        {toast.action === undefined ? null : (
          <button
            type="button"
            className={styles.action}
            onClick={() => {
              toast.action?.onAction();
              onDismiss(toast.id);
            }}
          >
            {toast.action.label}
          </button>
        )}
        <button
          type="button"
          className={styles.dismiss}
          onClick={() => {
            onDismiss(toast.id);
          }}
          aria-label={dismissLabel}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </div>
  );
}
